import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type ClientPolicyPeriod,
  ClientPolicyUnit,
  PayTypeEnum,
} from '@omnipresentgroup/types';
import type { JSONSchema7 } from 'json-schema';
import { useEffect, useState } from 'react';

const START_FIELD_TITLE = 'Start day of the month';
const END_FIELD_TITLE = 'Custom sick leave duration';
const UNIT_FIELD_TITLE = 'Unit';
const PAY_TYPE_FIELD_TITLE = 'Salary while on leave';
const FIXED_VALUE_FIELD_TITLE = 'Fixed value';

const UNIT_TOOLTIP_TEXT =
  'The unit is fixed to be in the same unit of the statutory leave in this country';
const PAY_DETAILS_TOOLTIP_TEXT =
  'Omnipresent recommends a custom policy of 100% pay to ensure a simple experience for employees';

export const useClientLeaveSettingFormSchema = ({
  initialFormValues,
  customMinimumConfig,
}: {
  initialFormValues?: ClientPolicyPeriod | null;
  customMinimumConfig?: ClientPolicyPeriod | null;
}) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState({});

  useEffect(() => {
    customMinimumConfig
      ? setSchema({
          type: 'object',
          description: '',
          required: ['end'],
          properties: {
            start: {
              title: START_FIELD_TITLE,
              type: 'number',
              default: initialFormValues?.start,
            },
            end: {
              title: END_FIELD_TITLE,
              type: 'number',
              default: initialFormValues?.end,
              minimum: customMinimumConfig.end,
            },
            unit: {
              title: UNIT_FIELD_TITLE,
              type: 'string',
              default: initialFormValues?.unit,
              oneOf: [
                {
                  const: ClientPolicyUnit.DAY,
                  title: toSentenceCase(ClientPolicyUnit.DAY),
                },
                {
                  const: ClientPolicyUnit.WEEK,
                  title: toSentenceCase(ClientPolicyUnit.WEEK),
                },
              ],
              readOnly: true,
            },
            payDetails: {
              type: 'object',
              properties: {
                payType: {
                  title: PAY_TYPE_FIELD_TITLE,
                  type: 'string',
                  default: initialFormValues?.payDetails.payType,
                  oneOf: [
                    {
                      const: PayTypeEnum.PERCENTAGE,
                      title: toSentenceCase(PayTypeEnum.PERCENTAGE),
                    },
                    {
                      const: PayTypeEnum.FIXED,
                      title: toSentenceCase(PayTypeEnum.FIXED),
                    },
                  ],
                  readOnly: true,
                },
                ...(customMinimumConfig.payDetails.payType ===
                  PayTypeEnum.PERCENTAGE && {
                  percentageValue: {
                    type: 'string',
                    default:
                      initialFormValues?.payDetails.payType ===
                      PayTypeEnum.PERCENTAGE
                        ? `${initialFormValues.payDetails.percentageValue}%`
                        : undefined,
                    readOnly: true,
                  },
                }),
                ...(customMinimumConfig.payDetails.payType ===
                  PayTypeEnum.FIXED && {
                  fixedValue: {
                    type: 'object',
                    properties: {
                      amount: {
                        title: FIXED_VALUE_FIELD_TITLE,
                        type: 'number',
                        default:
                          initialFormValues?.payDetails.payType ===
                          PayTypeEnum.FIXED
                            ? initialFormValues.payDetails.fixedValue.amount
                            : undefined,
                        readOnly: true,
                      },
                      currency: {
                        type: 'string',
                        default:
                          initialFormValues?.payDetails.payType ===
                          PayTypeEnum.FIXED
                            ? initialFormValues.payDetails.fixedValue.currency
                            : undefined,
                        readOnly: true,
                      },
                    },
                  },
                }),
              },
            },
          },
        })
      : setSchema({});

    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      start: {
        'ui:widget': 'hidden',
      },
      unit: {
        'ui:tooltip': UNIT_TOOLTIP_TEXT,
      },
      payDetails: {
        'ui:title': '',
        'ui:columns': {
          columns: 12,
          gap: 16,
        },
        payType: {
          'ui:title': '',
          'ui:span': 6,
          'ui:tooltip': PAY_DETAILS_TOOLTIP_TEXT,
        },
        percentageValue: {
          'ui:title': ' ',
          'ui:span': 6,
        },
        fixedValue: {
          'ui:title': '',
          'ui:span': 6,
          'ui:columns': {
            columns: 12,
            gap: 0,
          },
          amount: {
            'ui:span': 6,
          },
          currency: {
            'ui:title': ' ',
            'ui:span': 6,
          },
        },
      },
    });
  }, [
    customMinimumConfig,
    initialFormValues?.end,
    initialFormValues?.payDetails,
    initialFormValues?.start,
    initialFormValues?.unit,
  ]);

  return {
    schema,
    uiSchema,
  };
};
